/**
 * The three auth entry routes (#741) and how the pages cross-link them.
 *
 * `/login` and `/signup` share one form component, so every "other door" link
 * is built here rather than hardcoded at each call site. Two things this pins:
 *
 * - **"Sign in" from the sign-up page goes to the password form** (#1300):
 *   `/login?from=loginpage`, which has the email + password fields, Google,
 *   and a link to the one-time email link. Bare `/login` is the magic-link
 *   entry — a returning member who clicked "Sign in" expecting to type a
 *   password landed on an "enter your email" screen and read it as the wrong
 *   page.
 * - **A `?redirect=` or `?next=` the surrounding flow passed rides along** on
 *   every cross-link, so switching door (password ↔ magic link ↔ create
 *   account) does not lose where the user was headed (#1306 for `next`).
 */
export type AuthEntry = 'password' | 'magic-link' | 'signup'

const ENTRY_PATH: Record<AuthEntry, string> = {
  password: '/login',
  'magic-link': '/login',
  signup: '/signup',
}

export function authEntryHref(
  entry: AuthEntry,
  redirectParam: string | null | undefined = null,
  nextParam: string | null | undefined = null,
  // #1402 — a guest-checkout "create an account" prompt already knows the
  // buyer's email; carrying it here prefills the field on /login or /signup
  // instead of asking them to retype it. Signing in or up with this same
  // address then attaches their guest booking(s) automatically, via
  // POST /user/link-guest-bookings (every sign-in path calls it).
  emailParam: string | null | undefined = null,
): string {
  const params = new URLSearchParams()
  if (entry === 'password') params.set('from', 'loginpage')
  if (redirectParam) params.set('redirect', redirectParam)
  // #1306 — the return path (`/login?next=…` from checkout, bookings, the
  // receipt page) survives switching door too. `redirect` is the setup-intent
  // token (`host-signup` / `facilitator-signup`), a different thing.
  if (nextParam) params.set('next', nextParam)
  if (emailParam) params.set('email', emailParam)
  const query = params.toString()
  return `${ENTRY_PATH[entry]}${query ? `?${query}` : ''}`
}
